import { memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/system';
import { StationaryArea } from '@thive/fddm-types';

import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { getCommonNumber } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useHasOmniVehicles } from '@/modules/vehicles/hooks/useHasOmniVehicles';
import { CrabSettingInput } from '../common/CrabSettingInput';
import { getConnectionsWithSmoothHeading } from '../common/crabSettingConnections';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { usePropertyPanelConfirm } from '@/modules/common/components/usePropertyPanelConfirm';

type Props = {
  selectedStationaryAreas: StationaryArea[];
};

function StationaryPoiPreferencesComponent({ selectedStationaryAreas }: Props) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const historySvc = useMemo(() => HistoryServiceFactory.getInstance().createHistoryService(), []);
  const { confirm, Dialog } = usePropertyPanelConfirm();
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const hasOmniVehicles = useHasOmniVehicles();

  const crabSetting = useMemo(() => {
    return getCommonNumber(
      selectedStationaryAreas.flatMap(({ stationaryPoiSpecs }) =>
        stationaryPoiSpecs.map(({ crabSetting }) => crabSetting),
      ),
    );
  }, [selectedStationaryAreas]);

  const handleCrabChange = useCallback(
    async (value: number) => {
      const smoothHeading = value !== 0 ? true : fddmSvc.getPreference('connection.smoothHeading');
      const connectionsToUpdate = getConnectionsWithSmoothHeading(
        useFloorplanDesignStore
          .getState()
          .interAreaConnectionActions.getRelatedTo(selectedStationaryAreas.map(({ id }) => id)),
        smoothHeading,
      );

      // The connections aren't visible here, so ask before changing them
      if (connectionsToUpdate.length > 0) {
        const confirmed = await confirm(
          t('interface:properties.area.crab_setting_confirm.label'),
          t('interface:properties.area.crab_setting_confirm.message', {
            count: connectionsToUpdate.length,
          }),
        );
        if (!confirmed) return;
      }

      const updatedAreas = createEmptyAreas();
      selectedStationaryAreas.forEach((area) =>
        pushAreaToItsCollection(updatedAreas, {
          ...area,
          stationaryPoiSpecs: minItemsArrayUtils.mapAtLeast1(area.stationaryPoiSpecs, (spec) => ({
            ...spec,
            crabSetting: value,
          })),
        }),
      );

      historySvc.batch(() => {
        fddmSvc.setPreference('stationaryArea.stationaryPoiSpecs.crabSetting', value);
        fddmSvc.updateAreas(updatedAreas);
        if (connectionsToUpdate.length > 0) {
          fddmSvc.updateInterAreaConnections(connectionsToUpdate);
        }
      });
    },
    [confirm, fddmSvc, historySvc, selectedStationaryAreas, t],
  );

  if (!hasOmniVehicles) return null;

  return (
    <Stack spacing={1}>
      <Dialog anchor={ref} offset={[0, 100]} />
      <div ref={ref}>
        <PropertiesLabel i18nkey='interface:properties.area.crab_setting'>
          <CrabSettingInput value={crabSetting} onChange={handleCrabChange} />
        </PropertiesLabel>
      </div>
    </Stack>
  );
}

export const StationaryPoiPreferences = memo(StationaryPoiPreferencesComponent);
